import { ServiceError } from '../errors'
import type { ApiPendingToPaid } from './pendingToPaid'
import type { PaymentKind } from './types'

/** Límite de la espera del banco (D34): lo que dura como mucho un reto 3DS. La app actual no tenía límite. */
export const PAYMENT_WAIT_MS = 5 * 60 * 1000
/** Intervalo de consulta a E4, el de la app actual (useCardPayment.tsx). */
export const PAYMENT_POLL_MS = import.meta.env.MODE === 'test' ? 5 : 1000

/** `paidTypeId` de E3 según qué se paga (PaymentAdvance/Total/Partial/Deposit y ChannelValidation.tsx). */
export function paidTypeIdFor(kind: PaymentKind, api: Pick<ApiPendingToPaid, 'paidTypeAdvanceId' | 'paidTypeRestId' | 'paidTypeStandardId' | 'paidTypeBailId' | 'advancePrice' | 'advancePaid'>): string | undefined {
  switch (kind) {
    case 'advance':
      return api.paidTypeAdvanceId
    case 'rest':
      return api.paidTypeRestId
    case 'partial':
      return api.paidTypeStandardId
    case 'deposit':
      return api.paidTypeBailId
    case 'channel':
      return api.advancePrice && !api.advancePaid ? api.paidTypeAdvanceId : api.paidTypeRestId
  }
}

/** Comprobación previa al cobro de la app actual: el pago estándar (parcial) nunca se considera pagado. */
export function isAlreadyPaid(paidTypeId: string | undefined, api: Pick<ApiPendingToPaid, 'paidTypeRestId' | 'paidTypeBailId' | 'paidTypeAdvanceId' | 'restPaid' | 'bailPaid' | 'advancePaid'>): boolean {
  if (!paidTypeId) return false
  return (paidTypeId === api.paidTypeRestId && api.restPaid) || (paidTypeId === api.paidTypeBailId && api.bailPaid) || (paidTypeId === api.paidTypeAdvanceId && api.advancePaid)
}

/** Importe en céntimos → el texto que espera E3 (`"277.5"`, como los importes de E2). */
export const centsToPmsPrice = (cents: number) => String(Math.round(cents) / 100)

export function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(new ServiceError('server', 'aborted'))
    const onAbort = () => {
      clearTimeout(timer)
      reject(new ServiceError('server', 'aborted'))
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    signal?.addEventListener('abort', onAbort, { once: true })
  })
}

/**
 * Pago en curso guardado en la pestaña (D34): si la página se recarga o el banco devuelve a la app, la
 * pantalla de autorización retoma la espera de E4 con el mismo `temporalPaidId` en lugar de cobrar otra vez.
 */
export interface PendingPayment {
  guestCode: string
  /**
   * `change`: cobro de un cambio de reserva (bloque 5); la espera es la del cambio. `extras`: carrito de extras con
   * el contrato propuesto (bloque 6, solo mock); la espera es la del checkout
   */
  kind: PaymentKind | 'change' | 'extras'
  /** Cambio de reserva que se aplica cuando el banco autoriza (solo `change`) */
  changeId?: string
  /** Checkout de extras que se confirma cuando el banco autoriza (solo `extras`) */
  checkoutId?: string
  /** Ruta a la que volver si el banco rechaza o no responde; por defecto `returnTo` */
  retryTo?: string
  amount: number
  currency: string
  temporalPaidId: string
  iframeUrl: string
  /** Ruta a la que volver al terminar */
  returnTo: string
  startedAt: string
}

const PENDING_KEY = 'v2.pagos.enCurso'

export function readPendingPayment(guestCode: string): PendingPayment | null {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY)
    const value = raw ? (JSON.parse(raw) as PendingPayment) : null
    return value && value.guestCode === guestCode ? value : null
  } catch {
    return null
  }
}

export function writePendingPayment(value: PendingPayment | null): void {
  try {
    if (value) sessionStorage.setItem(PENDING_KEY, JSON.stringify(value))
    else sessionStorage.removeItem(PENDING_KEY)
  } catch {
    /* sin almacenamiento */
  }
}
